import { useMemo, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconEdit from '@/icons/Edit.svg';
import { ConditionFormFromGetMany, useConditionForms } from '@/models/conditionForm';

import { ConditionFormDialog } from './ConditionFormDialog';

export const ConditionFormsTable = () => {
  const { t } = useLingui();
  const { data: { conditionForms = [] } = {}, isLoading } = useConditionForms({
    types: ['initial_condition'],
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [conditionFormToEditId, setConditionFormToEditId] = useState<string | null>(null);

  const sortedConditionForms = useMemo(
    () =>
      [...conditionForms].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();

        return dateB - dateA;
      }),
    [conditionForms]
  );

  return (
    <Stack gap="24px">
      <Title size="200" weight="bold">
        <Trans id="settings.condition-forms.title">Initial Condition Forms</Trans>
      </Title>
      <Table
        ariaLabel={t({
          id: 'settings.condition-forms.aria-label',
          message: 'Condition Forms',
        })}
        columnWidths={['minmax(300px, auto)', 'auto', 'auto', 'auto']}
        variant="grid"
        bordered
      >
        {(isLoading || sortedConditionForms.length > 0) && (
          <Header>
            <Row>
              <Column>
                <Trans id="settings.condition-forms.column.questions">Questions</Trans>
              </Column>
              <Column>
                <Trans id="settings.condition-forms.column.workflows">Workflows</Trans>
              </Column>
              <Column>
                <Trans id="settings.condition-forms.column.updated">Last Updated</Trans>
              </Column>
              <Column>
                <Trans id="settings.condition-forms.column.actions">Actions</Trans>
              </Column>
            </Row>
          </Header>
        )}
        {sortedConditionForms.length > 0 ? (
          <Body>
            {sortedConditionForms.map((conditionForm) => (
              <ConditionFormRow
                key={conditionForm.id}
                conditionForm={conditionForm}
                onEdit={() => {
                  setConditionFormToEditId(conditionForm.id);
                  setIsDrawerOpen(true);
                }}
              />
            ))}
          </Body>
        ) : null}
      </Table>
      <ConditionFormDialog
        isOpen={isDrawerOpen}
        conditionFormId={conditionFormToEditId}
        onClose={() => {
          setConditionFormToEditId(null);
          setIsDrawerOpen(false);
        }}
      />
    </Stack>
  );
};

const ConditionFormRow = ({
  conditionForm,
  onEdit,
}: {
  conditionForm: ConditionFormFromGetMany;
  onEdit: () => void;
}) => {
  const questionsCount = conditionForm.questions.length;
  const workflowsCount = conditionForm.workflows.length;
  const updatedAt = new Date(conditionForm.updatedAt).toLocaleDateString();

  return (
    <Row>
      <Cell>
        <Stack>
          <InlineText weight="medium">
            <Plural
              id="settings.condition-forms.questions-count"
              value={questionsCount}
              one="# question"
              other="# questions"
            />
          </InlineText>
          <InlineText size="200" color="disabled">
            {conditionForm.id}
          </InlineText>
        </Stack>
      </Cell>
      <Cell>
        <InlineText size="200">
          {workflowsCount > 0 ? (
            <Plural
              id="settings.condition-forms.workflows-count"
              value={workflowsCount}
              one="# workflow"
              other="# workflows"
            />
          ) : (
            <InlineText color="secondary">
              <Trans id="settings.condition-forms.no-workflows">No workflows</Trans>
            </InlineText>
          )}
        </InlineText>
      </Cell>
      <Cell>
        <InlineText size="200">{updatedAt}</InlineText>
      </Cell>
      <Cell>
        <Tooltip content={<Trans id="settings.condition-forms.edit">Edit</Trans>}>
          <Button iconOnly size="small" variant="secondary" onPress={onEdit}>
            <IconEdit />
          </Button>
        </Tooltip>
      </Cell>
    </Row>
  );
};
